import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";

/**
 * Per-agent access gate. Admins always pass. Otherwise we call the
 * `user_has_agent_access` SECURITY DEFINER function which checks direct
 * user grants and organization-level grants (by email membership).
 */
export function useAgentAccess(agentId: string) {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (authLoading || roleLoading) return;
      if (!user) {
        setHasAccess(false);
        setLoading(false);
        return;
      }
      if (isAdmin) {
        setHasAccess(true);
        setLoading(false);
        return;
      }
      const { data, error } = await supabase.rpc("user_has_agent_access", {
        _user_id: user.id,
        _agent_id: agentId,
      });
      if (cancelled) return;
      setHasAccess(!error && Boolean(data));
      setLoading(false);
    }
    check();
    return () => { cancelled = true; };
  }, [user, isAdmin, authLoading, roleLoading, agentId]);

  return { hasAccess, loading: loading || authLoading || roleLoading };
}
